import { AngleDownLightIcon } from '@mm/icons';
import { LCView } from '@mm/state/actions/LiveChatCRUDActions';
import CompanyUsersModel from '@mm/state/model/CompanyUsersModel';
import {
  activeCompanyUsers,
  selectActiveUserId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useActor } from '@xstate/react';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { useContextSelector } from 'use-context-selector';
import SelectorWithArrow from '../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import { ContactContext, EmptySelectedContact } from '../../contactMachine';
import { LiveChatContext } from '../../LiveChatContext';
import './ConversationAssigneeFilterDropdown.pcss';

type ConversationAssigneeFilterDropdownOption = {
  id: LCView['selectedAssignee'];
  name: string;
};

export const ConversationAssigneeFilterDropdown = () => {
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );
  const activeUserId = useSelector(selectActiveUserId);
  const companyUsers = useSelector(activeCompanyUsers);

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const remoteFbPageRailsId = useSelector(
    (state: StoreState) => state.connectedPage.pageDetails?.id,
  );
  const { remoteUserId, selectedAssignee: assigneeFilterSetting = 'all' } =
    lcMachineState.context || {};

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const usersWithAccess = (user: CompanyUsersModel) => {
    if (
      !['facebook_page_collaborator', 'live_chat_agent'].find(
        r => r === user.roleCode,
      )
    ) {
      return true;
    }
    if (!remoteFbPageRailsId) {
      return true;
    }
    return (user.allowedFacebookPageIds || []).includes(remoteFbPageRailsId);
  };

  const assignmentOptions: ConversationAssigneeFilterDropdownOption[] = [
    { id: 'all', name: 'All' },
    { id: activeUserId, name: 'Assigned to me' },
    ...companyUsers
      .filter(u => u.id !== activeUserId)
      .filter(usersWithAccess)
      .map(u => ({
        id: u.id,
        name: `${u.firstName} ${u.lastName}`,
      })),
    { id: 'unassigned', name: 'Unassigned' },
  ];

  const customOptionRenderer = (
    option: ConversationAssigneeFilterDropdownOption,
    activeItem?: boolean,
  ) => {
    const user = companyUsers.find(u => u.id === option.id);
    const displayText =
      activeItem && user && user.id !== activeUserId
        ? `${user.firstName} ${user.lastName}`
        : option.name;
    return (
      <div className="conversation-assignee-filter-dropdown__option">
        <div
          className={
            activeItem
              ? 'conversation-assignee-filter-dropdown__option-display-name--active'
              : ''
          }
        >
          {displayText}
        </div>
      </div>
    );
  };

  const getSelectedOption = () => {
    if (assigneeFilterSetting) {
      return assignmentOptions.find(o => o.id === assigneeFilterSetting);
    }
    return assignmentOptions[assignmentOptions.length - 1];
  };

  const onChange = async (
    id: ConversationAssigneeFilterDropdownOption['id'],
  ) => {
    const viewUpdate = {
      selectedAssignee: id,
    };

    lcMachineSend({
      type: 'LC_VIEW_UPDATE',
      payload: { viewUpdate },
    });

    if (id !== 'all' && remoteUserId && selectedContact.conversation) {
      const { assignedUserId } = selectedContact.conversation;
      if ((id === 'unassigned' && !assignedUserId) || id !== assignedUserId) {
        lcMachineSend({ type: 'LC_REMOTE_USER_ID_CHANGE', payload: undefined });
      }
    }
  };

  const containerClassName = classNames({
    'conversation-assignee-filter-dropdown__container--active':
      assigneeFilterSetting !== 'all',
  });
  return (
    <div className={containerClassName}>
      <SelectorWithArrow
        className="conversation-assignee-filter-dropdown"
        items={assignmentOptions}
        selectedItem={getSelectedOption()}
        onChange={onChange}
        renderItem={customOptionRenderer}
        renderSelectedItem={item =>
          customOptionRenderer(
            item as ConversationAssigneeFilterDropdownOption,
            true,
          )
        }
        customArrowIcon={<AngleDownLightIcon />}
      />
    </div>
  );
};
